import React, { useMemo } from 'react';
import styled from 'styled-components';

const StyledGeometricPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
  opacity: ${props => props.opacity || 0.4};

  .pattern-container {
    position: absolute;
    width: 100%;
    height: 100%;
    background: ${props => props.backgroundColor || 'transparent'};
  }

  .geometric-shapes {
    position: absolute;
    width: 100%;
    height: 100%;
    opacity: ${props => props.shapesOpacity || 0.15};
  }

  .shape {
    position: absolute;
    border: 1.5px solid ${props => props.shapeColor || 'rgba(0, 0, 0, 0.1)'};
    background: ${props => props.shapeFill || 'transparent'};
    will-change: transform;
  }

  .circle {
    border-radius: 50%;
  }

  .dot {
    position: absolute;
    width: 4px;
    height: 4px;
    background: ${props => props.dotColor || 'rgba(0, 0, 0, 0.15)'};
    border-radius: 50%;
    will-change: transform;
  }
`;

const GeometricPattern = ({
  opacity = 0.4,
  shapesOpacity = 0.15,
  shapeColor = 'rgba(0, 0, 0, 0.1)',
  dotColor = 'rgba(0, 0, 0, 0.15)',
  className,
  style,
}) => {
  // Generate stable random positions for shapes using a seed
  const shapes = useMemo(() => {
    const seed = 12345; // Fixed seed for consistent pattern
    const shapesArray = [];
    
    // Simple seeded random function
    let seedValue = seed;
    const seededRandom = () => {
      seedValue = (seedValue * 9301 + 49297) % 233280;
      return seedValue / 233280;
    };

    // Generate circles - more visible
    for (let i = 0; i < 12; i++) {
      shapesArray.push({
        type: 'circle',
        size: seededRandom() * 250 + 120,
        x: seededRandom() * 100,
        y: seededRandom() * 100,
        rotation: seededRandom() * 360,
      });
    }

    // Generate rectangles - more visible
    for (let i = 0; i < 10; i++) {
      shapesArray.push({
        type: 'rect',
        width: seededRandom() * 180 + 100,
        height: seededRandom() * 180 + 100,
        x: seededRandom() * 100,
        y: seededRandom() * 100,
        rotation: seededRandom() * 45,
      });
    }

    return shapesArray;
  }, []);

  const dots = useMemo(() => {
    const seed = 67890;
    const dotsArray = [];
    let seedValue = seed;
    const seededRandom = () => {
      seedValue = (seedValue * 9301 + 49297) % 233280;
      return seedValue / 233280;
    };

    for (let i = 0; i < 80; i++) {
      dotsArray.push({
        x: seededRandom() * 100,
        y: seededRandom() * 100,
      });
    }

    return dotsArray;
  }, []);

  return (
    <StyledGeometricPattern
      className={className}
      style={style}
      opacity={opacity}
      shapesOpacity={shapesOpacity}
      shapeColor={shapeColor}
      dotColor={dotColor}>
      <div className="pattern-container">
        <div className="geometric-shapes">
          {shapes.map((shape, index) => (
            <div
              key={`shape-${index}`}
              className={`shape ${shape.type === 'circle' ? 'circle' : ''}`}
              style={{
                left: `${shape.x}%`,
                top: `${shape.y}%`,
                width: shape.type === 'circle' ? `${shape.size}px` : `${shape.width}px`,
                height: shape.type === 'circle' ? `${shape.size}px` : `${shape.height}px`,
                transform: `rotate(${shape.rotation}deg)`,
                transformOrigin: 'center',
              }}
            />
          ))}
          {dots.map((dot, index) => (
            <div
              key={`dot-${index}`}
              className="dot"
              style={{
                left: `${dot.x}%`,
                top: `${dot.y}%`,
              }}
            />
          ))}
        </div>
      </div>
    </StyledGeometricPattern>
  );
};

export default GeometricPattern;

